import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Trophy, Info, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcTools, bcCompare, bcCompareDimensions } from "@/lib/api";

const INDUSTRIES = [
  "dental", "medical", "restaurant", "real-estate", "automotive",
  "professional-services", "retail", "fitness", "general",
];
const SIZES = ["micro", "small", "medium", "enterprise"];
const CAPACITIES = ["low", "medium", "high"];

export default function BusinessCenterCompare() {
  const [params] = useSearchParams();
  const [tools, setTools] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [profile, setProfile] = useState({
    industry: "", business_size: "small",
    budget_monthly_usd: "", tech_capacity: "medium",
    priority_category: "", priority_integrations: "",
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Compare AI Tools | WebsiteTech.pro";
    bcTools().then((d) => setTools(d.items || []));
    bcCompareDimensions().then((d) => setDimensions(d.dimensions || []));
    const preset = params.get("tools");
    if (preset) setSelected(preset.split(",").slice(0, 4));
    const aid = params.get("assessment_id");
    if (aid) setProfile((p) => ({ ...p, assessment_id: aid }));
  }, [params]);

  const canRun = selected.length >= 2 && selected.length <= 4;
  const toggle = (slug) => {
    setSelected((cur) => {
      if (cur.includes(slug)) return cur.filter((s) => s !== slug);
      if (cur.length >= 4) return cur;
      return [...cur, slug];
    });
    setResult(null);
  };

  const run = async () => {
    setBusy(true); setError("");
    try {
      const body = {
        tool_slugs: selected,
        profile_override: {
          industry: profile.industry || null,
          business_size: profile.business_size || null,
          budget_monthly_usd: profile.budget_monthly_usd ? Number(profile.budget_monthly_usd) : null,
          tech_capacity: profile.tech_capacity || null,
          priority_category: profile.priority_category || null,
          priority_integrations: (profile.priority_integrations || "").split(",").map((s) => s.trim()).filter(Boolean),
        },
        assessment_id: profile.assessment_id || null,
      };
      const r = await bcCompare(body);
      setResult(r);
      window.scrollTo({ top: document.body.scrollHeight * 0.35, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Compare failed. Please try again.");
    } finally { setBusy(false); }
  };

  const winnerSlug = result?.winner_slug;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-compare">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Compare AI tools</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">Pick 2–4 tools, tell us about your business, and we'll compute a personalized <strong>Business Fit Score</strong> — every dimension shows exactly why it scored the way it did.</p>

          {/* Step 1: select tools */}
          <section className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-indigo-700">Step 1</div>
                <div className="text-lg font-bold text-slate-900">Pick 2–4 tools ({selected.length}/4)</div>
              </div>
              {selected.length > 0 && (
                <button onClick={() => { setSelected([]); setResult(null); }}
                  data-testid="bc-compare-clear"
                  className="text-xs font-semibold text-slate-500 hover:text-slate-800">Clear</button>
              )}
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tools.map((t) => {
                const isSelected = selected.includes(t.slug);
                const disabled = !isSelected && selected.length >= 4;
                return (
                  <button
                    key={t.slug}
                    onClick={() => toggle(t.slug)}
                    disabled={disabled}
                    data-testid={`bc-compare-toggle-${t.slug}`}
                    className={`text-left rounded-xl border p-4 transition ${
                      isSelected ? "border-indigo-500 bg-indigo-50 shadow-sm"
                                    : disabled ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                                                 : "border-slate-200 bg-white hover:border-indigo-300"
                    }`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold">{t.wt_score}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 uppercase tracking-widest">{t.category?.replaceAll("_", " ")}</div>
                    <div className="mt-2 text-xs text-slate-700 line-clamp-2">{t.tagline}</div>
                    {isSelected && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 2: profile / mini-quiz */}
          <section className="mt-8 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-indigo-700">Step 2</div>
            <div className="text-lg font-bold text-slate-900">Tell us about your business (30 seconds)</div>
            {profile.assessment_id && (
              <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-block">Using your saved assessment. Any fields below override it.</div>
            )}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Industry">
                <select value={profile.industry} onChange={(e) => setProfile((p) => ({ ...p, industry: e.target.value }))}
                  data-testid="bc-compare-industry" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option value="">— pick one —</option>
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
              <Field label="Business size">
                <select value={profile.business_size} onChange={(e) => setProfile((p) => ({ ...p, business_size: e.target.value }))}
                  data-testid="bc-compare-size" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Monthly budget for this tool (USD)">
                <input type="number" min="0" value={profile.budget_monthly_usd}
                  onChange={(e) => setProfile((p) => ({ ...p, budget_monthly_usd: e.target.value }))}
                  data-testid="bc-compare-budget"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="e.g. 300" />
              </Field>
              <Field label="Team tech capacity">
                <select value={profile.tech_capacity} onChange={(e) => setProfile((p) => ({ ...p, tech_capacity: e.target.value }))}
                  data-testid="bc-compare-capacity" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  {CAPACITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Priority category / capability">
                <input type="text" value={profile.priority_category}
                  onChange={(e) => setProfile((p) => ({ ...p, priority_category: e.target.value }))}
                  data-testid="bc-compare-priority-cat"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="e.g. AI Receptionist" />
              </Field>
              <Field label="Must-have integrations (comma-separated)">
                <input type="text" value={profile.priority_integrations}
                  onChange={(e) => setProfile((p) => ({ ...p, priority_integrations: e.target.value }))}
                  data-testid="bc-compare-priority-int"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="e.g. Twilio, Calendar" />
              </Field>
            </div>
            <div className="mt-6">
              <button onClick={run} disabled={!canRun || busy}
                data-testid="bc-compare-run"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3 text-white font-semibold transition">
                {busy ? "Computing…" : "Compute Business Fit Score"} <ArrowRight className="w-4 h-4" />
              </button>
              {error && <div className="mt-2 text-sm text-rose-700">{error}</div>}
            </div>
          </section>

          {/* Result */}
          {result && (
            <section className="mt-10" data-testid="bc-compare-result">
              <div className="rounded-2xl bg-slate-950 text-white p-6 flex items-center gap-4">
                <Trophy className="w-8 h-8 text-amber-400 flex-shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-400 font-bold">Winner</div>
                  <div className="text-xl md:text-2xl font-bold" data-testid="bc-compare-winner-name">
                    {result.results.find((r) => r.slug === winnerSlug)?.name}
                    <span className="ml-3 text-amber-400 text-lg">{result.winner_score} / 100</span>
                  </div>
                  <div className="text-sm text-slate-300 mt-1">Best-fit tool for the profile you provided. Every dimension below explains why.</div>
                </div>
              </div>

              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className="text-left text-xs uppercase tracking-widest text-slate-500 font-bold py-2 sticky left-0 bg-slate-50">Dimension</th>
                      {result.results.map((r) => (
                        <th key={r.slug} className={`text-left px-3 py-2 border-b border-slate-200 ${r.slug === winnerSlug ? "bg-amber-50" : "bg-white"}`}>
                          <div className="text-sm font-bold text-slate-900">{r.name}</div>
                          <div className="text-[10px] uppercase text-slate-500">{r.vendor}</div>
                          <div className="mt-1 text-lg font-black text-indigo-700" data-testid={`bc-compare-overall-${r.slug}`}>{r.business_fit.overall} <span className="text-xs text-slate-400 font-normal">/ 100</span></div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dimensions.map((d) => (
                      <tr key={d.key} className="align-top">
                        <td className="py-3 px-2 text-slate-700 font-semibold sticky left-0 bg-slate-50 border-b border-slate-100">
                          {d.label}
                          <div className="text-[10px] font-normal text-slate-400">weight {d.weight}</div>
                        </td>
                        {result.results.map((r) => {
                          const dim = r.business_fit.dimensions.find((x) => x.key === d.key);
                          return (
                            <td key={r.slug} className={`p-3 border-b border-slate-100 ${r.slug === winnerSlug ? "bg-amber-50/50" : ""}`}>
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-bold text-slate-900">{dim?.score}</div>
                                <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                  <div className="h-full bg-indigo-500" style={{ width: `${dim?.score || 0}%` }} />
                                </div>
                              </div>
                              <div className="mt-1 text-[11px] text-slate-600 leading-snug">{dim?.reason}</div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr>
                      <td className="py-3 px-2 text-slate-700 font-semibold sticky left-0 bg-slate-50">Pricing (published)</td>
                      {result.results.map((r) => (
                        <td key={r.slug} className={`p-3 text-sm text-slate-700 ${r.slug === winnerSlug ? "bg-amber-50/50" : ""}`}>
                          {r.pricing_summary || "Contact vendor"}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-3 px-2 text-slate-700 font-semibold sticky left-0 bg-slate-50">Signature features</td>
                      {result.results.map((r) => (
                        <td key={r.slug} className={`p-3 text-xs text-slate-700 ${r.slug === winnerSlug ? "bg-amber-50/50" : ""}`}>
                          <ul className="space-y-0.5">{(r.features || []).slice(0, 4).map((f) => <li key={f}>· {f}</li>)}</ul>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4 text-xs text-slate-600 flex items-start gap-2">
                <Info className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  Business Fit Score is computed from a weighted set of 7 dimensions with your profile as input.
                  It is an <strong>opinion</strong>, not a guarantee. Every score exposes its reasoning — click any cell to see why.
                  Commercial relationships with any tool are declared in the <Link to="/business-center/methodology" className="text-indigo-700 font-semibold">methodology</Link>.
                </div>
              </div>

              <div className="mt-4 rounded-2xl bg-slate-950 text-white p-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-bold">Ready to move forward with {result.results.find((r) => r.slug === winnerSlug)?.name}?</div>
                  <div className="text-sm text-slate-300 mt-1">We'll turn this into a proposal tailored to your business.</div>
                </div>
                <Link to="/contact" data-testid="bc-compare-request-implementation"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-5 py-3 font-semibold transition">
                  Request implementation <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
